import { useQuery } from '@tanstack/react-query';
import { creditPackSchema } from '@xdocs/schemas';
import type { CreditPack, WsOp } from '@xdocs/schemas';
import { useWs } from '../../../ws/WsProvider.js';

/** Not (yet) a member of the frozen `wsOp` enum — see the hook's doc
 * comment below for why this cast is necessary and temporary. */
const CREDIT_PACKS_LIST_OP = 'billing.creditPacks.list' as unknown as WsOp;

// `.array()` (not a bare `zod` import — not a direct dependency of
// `apps/web`) turns the single-row schema into the list-response validator.
const creditPackListResult = creditPackSchema.array();

export interface UseCreditPacksResult {
  packs: CreditPack[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/**
 * F57 credit-packs grid (`credit_packs` catalog).
 *
 * CONTRACT GAP (flagged in this task's WIRING.md — not fixable from
 * `apps/web/**`): there is NO op anywhere in the frozen contract
 * (`packages/schemas/src/ws.ts` `wsOp` enum / `packages/ws-protocol/src/
 * op-contracts.ts` `WsOpContracts`) that lists the `credit_packs` catalog —
 * F48's `/pricing/credits` (out of this task's route ownership) and this
 * section both need one, e.g. `billing.creditPacks.list: { request:
 * undefined; response: CreditPack[] }`. Until that op exists server-side
 * AND is added to the frozen contract, this calls the raw `WsClient.
 * request()` escape hatch with a NOT-YET-REGISTERED op name — every server
 * currently rejects it with `BAD_OP`, so this hook always resolves to the
 * `isError` state (a real, honest error — never fake/hardcoded pack data).
 * Swap to `requestTyped(wsClient, 'billing.creditPacks.list', undefined)`
 * once the op lands; no other change needed here.
 */
export function useCreditPacks(): UseCreditPacksResult {
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected';

  const query = useQuery<CreditPack[]>({
    queryKey: ['billing', 'creditPacks', 'list'],
    queryFn: async () => {
      const raw = await wsClient.request(CREDIT_PACKS_LIST_OP, undefined);
      return creditPackListResult.parse(raw);
    },
    enabled,
  });

  return {
    packs: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    onRetry: () => void query.refetch(),
  };
}
